import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  Cell, Legend, Pie, PieChart,
} from 'recharts';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Typography from 'Components/UI/Typography';

const useStyles = buildUseStyles('DonutChart', ((theme) => ({
  root: {},
  legendTitle: {
    marginBottom: theme.spacing(1),
  },
  legendItem: {
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2.5),
  },
})));

const getColors = theme => [
  theme.palette.facet.primaryBlue,
  theme.palette.success.main,
  theme.palette.facet.accentBlue,
  theme.palette.facet.primaryBlueMuted,
];

const DefaultLegend = ({ payload, legendTitle }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  return (
    <Box>
      {legendTitle && (
        <Typography
          className={classes.legendTitle}
          color={'primary'}
          weight={'book'}
          variant={'h4'}
        >
          {legendTitle}
        </Typography>
      )}
      {payload.map(entry => (
        <Typography
          key={entry.value}
          className={classes.legendItem}
          style={{ color: entry.color }}
          weight={'book'}
          variant={'h4'}
        >
          {entry.value}
        </Typography>
      ))}
    </Box>
  );
};

DefaultLegend.defaultProps = {
  legendTitle: '',
};

DefaultLegend.propTypes = {
  payload: PropTypes.array.isRequired,
  legendTitle: PropTypes.string,
};

const DonutChart = ({
  className,
  data,
  dataKey,
  width,
  height,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  legendTitle,
  customLegend: CustomLegend,
}) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const classes = useStyles(theme);

  return (
    <PieChart className={classNames(classes.root, className)} width={width} height={height}>
      <Legend
        layout={'vertical'}
        verticalAlign={'middle'}
        content={(props) => <CustomLegend legendTitle={legendTitle} {...props} />}
      />
      <Pie
        data={data}
        dataKey={dataKey}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        isAnimationActive
      >
        {data.map((dataPoint, i) => (
          <Cell
            key={dataPoint.name}
            fill={dataPoint.color || colors[i % colors.length]}
          />
        ))}
      </Pie>
    </PieChart>
  );
};

DonutChart.defaultProps = {
  className: '',
  dataKey: 'value',
  width: 250,
  height: 250,
  innerRadius: 100,
  outerRadius: 122,
  startAngle: 90,
  endAngle: -270,
  legendTitle: '',
  customLegend: DefaultLegend,
};

DonutChart.propTypes = {
  className: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
  })).isRequired,
  dataKey: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  innerRadius: PropTypes.number,
  outerRadius: PropTypes.number,
  startAngle: PropTypes.number,
  endAngle: PropTypes.number,
  legendTitle: PropTypes.string,
  customLegend: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.node]),
};

export default DonutChart;
